import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import type { TFunction } from 'i18next';
import { useLogin } from '../hooks/use-login';

const getFormSchema = (t: TFunction) =>
  z.object({
    email: z
      .string({
        required_error: t('auth.login.form.fields.email.error.required'),
      })
      .email({
        message: t('auth.login.form.fields.email.error.invalid'),
      }),
    password: z
      .string({
        required_error: t('auth.login.form.fields.password.error.required'),
      })
      .min(6, {
        message: t('auth.login.form.fields.password.error.min'),
      }),
  });

interface LoginFormProps {
  invitationFlow: {
    invitationEmail?: string;
    organizationId?: string;
    invitationCode?: string;
  };
  email?: string;
  redirect?: string;
}

export function LoginForm({ invitationFlow, email, redirect }: LoginFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const login = useLogin();
  const { t } = useTranslation();

  const formSchema = getFormSchema(t);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: email ?? '',
      password: '',
    },
  });

  useEffect(() => {
    if (invitationFlow?.invitationEmail) {
      form.setValue('email', invitationFlow.invitationEmail);
    }
  }, [form, invitationFlow?.invitationEmail]);

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);

    const response = await login(values, {
      invitationCode: invitationFlow?.invitationCode,
      organizationId: invitationFlow?.organizationId,
      redirect,
    });

    setIsLoading(false);

    if (response.isErr()) {
      if (response.error.type === 'INVALID_CREDENTIALS') {
        form.setError('email', {
          message: t('auth.login.form.fields.email.error.invalidCredentials'),
        });
      } else {
        toast.error(t('auth.login.form.toast.genericError'));
      }
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4"
        data-testid="login-form"
      >
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('auth.login.form.fields.email.label')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('auth.login.form.fields.email.placeholder')}
                  type="email"
                  {...field}
                  disabled={!!invitationFlow?.invitationEmail}
                  data-testid="login-email-input"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between">
                <FormLabel>
                  {t('auth.login.form.fields.password.label')}
                </FormLabel>
                <Link
                  to="/reset-password"
                  className="text-sm text-primary hover:underline"
                >
                  {t('auth.login.form.fields.password.hint')}
                </Link>
              </div>
              <FormControl>
                <PasswordInput
                  placeholder={t('auth.login.form.fields.password.placeholder')}
                  {...field}
                  data-testid="login-password-input"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          className="w-full"
          disabled={isLoading}
          data-testid="login-submit-button"
          isLoading={isLoading}
        >
          {t('auth.login.form.submit')}
        </Button>
      </form>
    </Form>
  );
}
